import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Upload, FolderOpen, Trash2, Download } from 'lucide-react';
import { COMPANY } from '../../../config/company';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * ClientDocumentsTab - Displays and manages client documents
 */
const ClientDocumentsTab = ({
  language,
  client,
  docInputRef,
  uploadingDoc,
  handleUploadDocument,
  handleDeleteDocument,
  formatDate
}) => {
  // Format file size for display
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Card className="bg-white border-gray-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-gray-900">
          {language === 'es' ? 'Documentos' : 'Documents'}
        </CardTitle>
        <div>
          <input 
            type="file" 
            ref={docInputRef} 
            onChange={handleUploadDocument} 
            className="hidden" 
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic" 
          />
          <Button 
            onClick={() => docInputRef.current?.click()} 
            disabled={uploadingDoc} 
            style={{ backgroundColor: COMPANY.primaryColor }} 
            className="text-gray-900"
          >
            <Upload className="mr-2 h-4 w-4" />
            {uploadingDoc 
              ? (language === 'es' ? 'Subiendo...' : 'Uploading...') 
              : (language === 'es' ? 'Subir' : 'Upload')}
          </Button>
          <p className="text-xs text-gray-500 mt-1">
            {language === 'es' ? 'PDF, Word, Imágenes' : 'PDF, Word, Images'}
          </p>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {client.documents?.map(doc => (
            <div 
              key={doc.id} 
              className="p-4 rounded-lg bg-gray-100 border border-gray-200"
            >
              <div className="flex items-start justify-between mb-2">
                <FolderOpen className="h-8 w-8 text-amber-500" />
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-red-400 hover:text-red-300 h-6 w-6 p-0" 
                  onClick={() => handleDeleteDocument(doc.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <p 
                className="text-gray-900 font-medium truncate" 
                title={doc.original_filename || doc.title}
              >
                {doc.original_filename || doc.title}
              </p>
              {doc.description && (
                <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                  {doc.description}
                </p>
              )}
              <p className="text-gray-500 text-sm">{formatFileSize(doc.file_size)}</p>
              <p className="text-gray-500 text-xs">{formatDate(doc.created_at)}</p>
              <a 
                href={doc.gridfs_id 
                  ? `${BACKEND_URL}/api/files/download/${doc.gridfs_id}` 
                  : `${BACKEND_URL}/api/uploads/documents/${doc.filename}`} 
                target="_blank" 
                rel="noreferrer" 
                download={doc.original_filename || doc.title}
              >
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="w-full mt-3 border-gray-300 text-gray-600"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Descargar' : 'Download'}
                </Button>
              </a>
            </div>
          ))}
          {!client.documents?.length && (
            <div className="col-span-full text-center py-8">
              <FolderOpen className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">
                {language === 'es' ? 'Sin documentos' : 'No documents yet'}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientDocumentsTab;
